import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react'
import { ToastContext } from './toastContext'
import { attachToast, detachToast } from '../../lib/toast'
import './ui.css'

const MAX_VISIBLE = 4

const KIND_ICONS = {
  info: <Info size={16} />,
  success: <CheckCircle2 size={16} />,
  error: <AlertCircle size={16} />,
}

export function ToastProvider({ children, duration = 4200 }) {
  const [toasts, setToasts] = useState([])
  const timers = useRef(new Map())

  const clearTimer = useCallback((id) => {
    const handle = timers.current.get(id)
    if (handle) {
      clearTimeout(handle)
      timers.current.delete(id)
    }
  }, [])

  const dismiss = useCallback(
    (id) => {
      clearTimer(id)
      setToasts((prev) => prev.filter((t) => t.id !== id))
    },
    [clearTimer]
  )

  const toast = useCallback(
    (message, kind = 'info') => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      setToasts((prev) => [...prev, { id, message, kind }].slice(-MAX_VISIBLE))
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        )
      }
      return id
    },
    [dismiss, duration]
  )

  useEffect(() => {
    attachToast({ toast, dismiss })
    return () => detachToast()
  }, [toast, dismiss])

  useEffect(
    () => () => {
      timers.current.forEach((handle) => clearTimeout(handle))
      timers.current.clear()
    },
    []
  )

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      <div className="ui-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`ui-toast ui-toast--${t.kind}`}
            role={t.kind === 'error' ? 'alert' : 'status'}
            aria-atomic="true"
          >
            <span className="ui-toast-icon" aria-hidden="true">
              {KIND_ICONS[t.kind] || KIND_ICONS.info}
            </span>
            <span className="ui-toast-text">{t.message}</span>
            <button
              type="button"
              className="ui-toast-close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
            {duration > 0 ? (
              <span
                className="ui-toast-timer"
                style={{ '--ui-toast-duration': `${duration}ms` }}
                aria-hidden="true"
              />
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export default ToastProvider
